import 'slick-carousel/slick/slick-theme.css'
import 'slick-carousel/slick/slick.css'

import { useMediaQuery } from '@mantine/hooks'
import Slider, { Settings } from 'react-slick'
import Image from 'next/image'
import styles from './kdt-growth-2nd.module.scss'
import Link from 'next/link'

interface ICurriculumBenefitProps {
  contents: {
    imgUrlM: string
    title: string
    desc: string
    descList?: {
      linkText: string
      linkUrl: string
    }[]
  }[]
}

export const CurriculumBenefitSliderItem = ({ contents }: ICurriculumBenefitProps) => {
  const settings: Settings = {
    dots: true,
    arrows: false,
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    speed: 500,
    centerMode: true,
    centerPadding: '24px',
    touchMove: true,
    variableWidth: false,
  }

  return (
    <Slider {...settings}>
      {contents.map(({ imgUrlM, title, desc, descList }) => (
        <div key={title} className="px-1 pb-8">
          <div className="h-[402px] w-full overflow-hidden rounded-lg bg-trueGray-800">
            <div className="relative w-full">
              <img className="h-[164px] w-full object-cover" src={imgUrlM} alt="-" />
            </div>
            <div className="flex flex-col p-4">
              <h6 className="mb-3 whitespace-pre-line text-lg font-bold leading-normal text-yellow-450">
                {title}
              </h6>
              <p className="whitespace-pre-line text-base leading-normal text-white">{desc}</p>
              {descList && (
                <div className="mt-2 flex flex-col gap-2">
                  {descList?.map(item => (
                    <p
                      key={`${title}-${item.linkText}`}
                      className={`${styles.text_dot} ${styles.text_dot_section7} text-sm leading-normal text-label-assistive`}
                    >
                      <Link href={item.linkUrl} target="_blank" className="underline">
                        {item.linkText}
                      </Link>
                    </p>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </Slider>
  )
}

interface ISupportBenefitProps {
  contents: {
    imgUrlM: string
    imgUrlPc: string
    step: string
    title: string
    desc?: string
    descList?: string[]
    point?: {
      pointTitle: string
      pointDesc: string
    }[]
  }[]
}

export const SupportBenefitSliderItem = ({ contents }: ISupportBenefitProps) => {
  const settings: Settings = {
    dots: true,
    arrows: false,
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    speed: 500,
    centerMode: false,
    touchMove: true,
    variableWidth: true,
  }

  return (
    <Slider {...settings}>
      {contents.map(supportBenefit => (
        <div key={supportBenefit.title} className="pb-8 pl-4">
          <div className="flex w-full flex-col rounded-lg bg-trueGray-800">
            <div className="h-[136px] w-full object-cover">
              <Image
                src={supportBenefit.imgUrlM}
                layout="fill"
                objectFit="cover"
                className="!relative w-full"
                alt={supportBenefit.title}
              />
            </div>
            <div className="flex h-[230px] w-full flex-col items-start justify-start p-4">
              <div className="mb-3 flex w-full flex-row items-center gap-1.5 border-b border-[#404040] pb-3">
                <p className="max-w-fit rounded-sm bg-[rgba(255,119,16,0.2)] px-1 py-0.5 text-xs font-bold text-yellow-450">
                  {supportBenefit.step}
                </p>
                <h6 className="text-base leading-normal text-white">{supportBenefit.title}</h6>
              </div>

              {supportBenefit.desc && (
                <p className="w-full whitespace-pre-line text-sm leading-normal text-label-assistive">
                  {supportBenefit.desc}
                </p>
              )}
              {supportBenefit.descList && (
                <ul className="flex w-full flex-col gap-0.5">
                  {supportBenefit.descList.map(item => (
                    <li key={`${supportBenefit.title}-${item}`}>
                      <p
                        className={`${styles.text_dot} ${styles.text_dot_section10} text-sm leading-normal text-label-assistive`}
                      >
                        {item}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
              {supportBenefit.point && (
                <div className="mt-3 flex w-full flex-1 flex-col gap-1.5 rounded bg-white px-4 py-3">
                  {supportBenefit.point.map(item => (
                    <div key={`${supportBenefit.title}-${item.pointTitle}`}>
                      <p className="text-sm font-bold text-label-normal">{item.pointTitle}</p>
                      <p className="text-sm leading-normal text-label-normal">{item.pointDesc}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </Slider>
  )
}

interface IBenefitProps {
  contents: {
    imgUrlM: string
    imgUrlPc: string
    title: string
    desc: string
  }[]
}

export const BenefitSliderItem = ({ contents }: IBenefitProps) => {
  const isMobile = useMediaQuery('(max-width: 1023px)')
  const settings: Settings = {
    dots: false,
    arrows: true,
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    speed: 500,
    centerMode: false,
    touchMove: true,
    variableWidth: true,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          dots: true,
          arrows: false,
          slidesToShow: 1,
          touchMove: true,
          centerMode: false,
          variableWidth: true,
        },
      },
    ],
  }

  return (
    <Slider {...settings}>
      {contents.map(benefit => (
        <div key={benefit.title} className="pb-8 pl-4 lg:pb-[100px] lg:pl-6">
          <div className="flex h-[296px] w-full flex-col overflow-hidden lg:h-[420px] lg:rounded-lg lg:bg-[#292625]">
            <div className="h-[174px] w-full overflow-hidden rounded-sm bg-gray-900 object-cover lg:h-[240px]">
              <Image
                src={isMobile ? benefit.imgUrlM : benefit.imgUrlPc}
                layout="fill"
                objectFit="cover"
                className="!relative w-full"
                alt={benefit.title}
              />
            </div>
            <div className="flex w-full flex-1 flex-col items-start justify-start gap-2 pt-[18px] lg:p-6">
              <h6 className="w-full text-base font-bold leading-normal text-white lg:text-xl">
                {benefit.title}
              </h6>
              <p className="w-full whitespace-pre-line text-base leading-normal text-label-assistive">
                {benefit.desc}
              </p>
            </div>
          </div>
        </div>
      ))}
    </Slider>
  )
}

interface IReviewProps {
  contents: {
    imgUrl: string
    review: string
  }[]
}

export const ReviewSliderItem = ({ contents }: IReviewProps) => {
  const settings: Settings = {
    dots: true,
    arrows: false,
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    speed: 500,
    centerMode: false,
    touchMove: true,
    variableWidth: true,
  }

  return (
    <Slider {...settings}>
      {contents.map(review => (
        <div key={review.imgUrl} className="pb-8 pl-4">
          <div className="flex h-[232px] w-full flex-col gap-4 rounded-lg bg-trueGray-800 p-4">
            <div className="h-12 w-12 object-cover">
              <Image
                src={review.imgUrl}
                layout="fill"
                objectFit="cover"
                className="!relative w-full"
                alt="-"
              />
            </div>
            <div className="flex w-full flex-col items-start justify-start border-t border-[#404040] pt-4">
              <p className="w-full text-base font-bold leading-normal text-white">
                {review.review}
              </p>
            </div>
          </div>
        </div>
      ))}
    </Slider>
  )
}
